import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnDestroy
} from '@angular/core';

@Directive({
    selector: '[appPanzoom]',
    exportAs: 'appPanzoom',
    standalone: false
})
export class PanzoomDirective implements AfterViewInit, OnDestroy {
  @Input() maxScale = 6;
  @Input() disabled = false;

  private wrapper?: HTMLElement;

  private scale = 1;
  private minScale = 1;
  private x = 0;
  private y = 0;

  private isDragging = false;
  private lastPointerX = 0;
  private lastPointerY = 0;

  private wheelHandler = (event: WheelEvent) => this.onWheel(event);
  private pointerDownHandler = (event: PointerEvent) => this.onPointerDown(event);
  private pointerMoveHandler = (event: PointerEvent) => this.onPointerMove(event);
  private pointerUpHandler = () => this.onPointerUp();


  constructor(private readonly el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    this.wrapper =
      this.el.nativeElement.closest('.image-wrapper') as HTMLElement | null ?? undefined;

    if (!this.wrapper) {
      return;
    }

    this.wrapper.addEventListener('wheel', this.wheelHandler, {
      passive: false
    });

    this.wrapper.addEventListener('pointerdown', this.pointerDownHandler);
    window.addEventListener('pointermove', this.pointerMoveHandler);
    window.addEventListener('pointerup', this.pointerUpHandler);
    window.addEventListener('pointercancel', this.pointerUpHandler);
  }

  fitToWrapper(): void {
    if (!this.wrapper) {
      return;
    }

    const img = this.el.nativeElement.querySelector('img') as HTMLImageElement | null;

    if (!img || !img.naturalWidth || !img.naturalHeight) {
      return;
    }

    const wrapperWidth = this.wrapper.clientWidth;
    const wrapperHeight = this.wrapper.clientHeight;

    const contentWidth = this.el.nativeElement.offsetWidth;
    const contentHeight = this.el.nativeElement.offsetHeight;

    const imageWidth = img.naturalWidth;
    const imageHeight = img.naturalHeight;

    if (!wrapperWidth || !wrapperHeight || !imageWidth || !imageHeight) {
      return;
    }

    this.el.nativeElement.style.width = `${imageWidth}px`;
    this.el.nativeElement.style.height = `${imageHeight}px`;

    this.minScale = Math.min(
      wrapperWidth / imageWidth,
      wrapperHeight / imageHeight
    );

    const fitScale = Math.min(
      wrapperWidth / contentWidth,
      wrapperHeight / contentHeight
    );

    this.scale = fitScale;
    this.minScale = fitScale;

    this.x = (wrapperWidth - contentWidth * fitScale) / 2;
    this.y = (wrapperHeight - contentHeight * fitScale) / 2;

    this.applyTransform();
  }

  reset(): void {
    this.fitToWrapper();
  }

  private onWheel(event: WheelEvent): void {
    if (this.disabled || !this.wrapper) {
      return;
    }

    event.preventDefault();

    const rect = this.wrapper.getBoundingClientRect();

    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const pointX = (mouseX - this.x) / this.scale;
    const pointY = (mouseY - this.y) / this.scale;

    const zoomFactor = event.deltaY < 0 ? 1.12 : 0.88;

    const nextScale = this.clamp(
      this.scale * zoomFactor,
      this.minScale,
      this.maxScale
    );

    this.x = mouseX - pointX * nextScale;
    this.y = mouseY - pointY * nextScale;
    this.scale = nextScale;

    this.applyTransform();
  }

  private onPointerDown(event: PointerEvent): void {
    if (this.disabled || event.button !== 0) {
      return;
    }

    this.isDragging = true;
    this.lastPointerX = event.clientX;
    this.lastPointerY = event.clientY;

    this.wrapper?.classList.add('is-dragging');
  }

  private onPointerMove(event: PointerEvent): void {
    if (!this.isDragging || this.disabled) {
      return;
    }

    const dx = event.clientX - this.lastPointerX;
    const dy = event.clientY - this.lastPointerY;

    this.lastPointerX = event.clientX;
    this.lastPointerY = event.clientY;

    this.x += dx;
    this.y += dy;

    this.applyTransform();
  }

  private onPointerUp(): void {
    this.isDragging = false;
    this.wrapper?.classList.remove('is-dragging');
  }

  private applyTransform(): void {
    this.el.nativeElement.style.transform =
      `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  ngOnDestroy(): void {
    if (this.wrapper) {
      this.wrapper.removeEventListener('wheel', this.wheelHandler);
      this.wrapper.removeEventListener('pointerdown', this.pointerDownHandler);
    }

    window.removeEventListener('pointermove', this.pointerMoveHandler);
    window.removeEventListener('pointerup', this.pointerUpHandler);
    window.removeEventListener('pointercancel', this.pointerUpHandler);
  }
}
